// Copyright 2021 Observable, Inc.
// Released under the ISC license.

import * as d3 from "d3";
import * as d3Zoom from "d3-zoom";

import React, { useRef, useEffect, useState, useMemo } from "react";
import { colourMaps } from "../../utils/colourMaps";
import { useD3Graph } from "../../hooks/useD3";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

export default function GraphChart({
  micro_id,
  nodes,
  edges,
  hoverToggled,
  geneticMatchSeqIds = [],
  geneticHighlightEnabled = false,
  nodeId = (d) => d.id,
  nodeTitle = (d) => d.name,
  nodeInfStart = (d) => d.inf_start,
  nodeSeqId = (d) => d.seq_id,
  nodeCollectDt = (d) => d.collectdt,
  edgeSource = ({ source }) => source,
  edgeTarget = ({ target }) => target,
  edgeDuration = ({ duration }) => duration,
  edgeWard = ({ ward }) => ward,
  edgeHospital = ({ hospital }) => hospital,
  edgeSpecialty = ({ specialty }) => specialty,
  nodeStrength,
  edgeStrength,
}) {
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });

  console.log("nodes", nodes);

  useEffect(() => {
    function updateSize() {
      if (containerRef.current) {
        const { clientWidth, clientHeight } = containerRef.current;
        setDimensions({
          width: clientWidth,
          height: clientHeight,
        });
      }
    }

    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  const { width, height } = dimensions;

  var edge_width_param = useSelector(
    (state) => state.patient.graph.edge_width_param
  );
  var edge_colour_param = useSelector(
    (state) => state.patient.graph.edge_colour_param
  );

  var highlightPhylos = useSelector(
    (state) => state.patient.graph.highlightPhylos
  );

  const navigate = useNavigate();

  const N = d3.map(nodes, nodeId).map(intern);
  const ES = d3.map(edges, edgeSource).map(intern);
  const ET = d3.map(edges, edgeTarget).map(intern);
  const ED = d3.map(edges, edgeDuration).map(intern);
  const EW = d3.map(edges, edgeWard).map(intern);
  const EH = d3.map(edges, edgeHospital).map(intern);
  const ESP = d3.map(edges, edgeSpecialty).map(intern);

  const T = d3.map(nodes, nodeTitle);
  const IS = d3.map(nodes, nodeInfStart);
  const SID = d3.map(nodes, nodeSeqId);
  const CD = d3.map(nodes, nodeCollectDt);

  function intern(value) {
    return value !== null && typeof value === "object"
      ? value.valueOf()
      : value;
  }

  nodes = d3.map(nodes, (_, i) => ({
    id: N[i],
    name: T[i],
    inf_start: IS[i],
    seq_id: SID[i] !== null && SID[i] !== undefined ? String(SID[i]) : null,
    collectdt: CD[i],
  }));
  edges = d3.map(edges, (_, i) => ({
    source: ES[i],
    target: ET[i],
  }));

  // Precompute shell distance (layers) from index patient for colouring
  const adjacency = new Map();
  edges.forEach((e) => {
    const s = String(e.source);
    const t = String(e.target);
    if (!adjacency.has(s)) adjacency.set(s, []);
    if (!adjacency.has(t)) adjacency.set(t, []);
    adjacency.get(s).push(t);
    adjacency.get(t).push(s);
  });
  const shellDistance = new Map();
  const bfs = () => {
    const start = String(micro_id);
    const visited = new Set([start]);
    let frontier = [start];
    let distance = 0;
    shellDistance.set(start, 0);
    while (frontier.length) {
      const next = [];
      for (const node of frontier) {
        const nbrs = adjacency.get(node) || [];
        for (const n of nbrs) {
          if (!visited.has(n)) {
            visited.add(n);
            shellDistance.set(n, distance + 1);
            next.push(n);
          }
        }
      }
      distance += 1;
      frontier = next;
    }
  };
  bfs();
  const maxShell =
    shellDistance.size > 0
      ? Math.max(...Array.from(shellDistance.values()))
      : 0;

  // Colour-blind-friendly (Okabe–Ito inspired) palette + neutral UI tones.
  const palette = {
    text: "#0b1b36",
    textIndex: "#9a3412",
    tooltipBg: "#132f52",
    tooltipText: "white",
    tooltipBorder: "black",

    index: "#c23c2a",

    // shellNodeNear: "#3b95e3ff",
    // shellNodeFar: "#97c8fcff",
    // shellEdgeNear: "rgba(45, 119, 184, 0.78)",
    // shellEdgeFar: "rgba(168, 210, 237, 0.71)",

    shellNodeNear: "#6c8fd0ff",
    shellNodeFar: "#acc3eeff",

    shellEdgeNear: "#5f7aacff",
    shellEdgeFar: "#a8b9d8ff",

    geneticStroke: "#7b3fb4",
    geneticFill: "#d3adf4ff",

    nodeStrokeNear: "#1c4797ff",
    nodeStrokeFar: "#5682d3ff",
  };

  const shellColourScale = (depth) => {
    const d = Math.max(0, Math.min(Number(depth) || 0, maxShell));
    const denom = Math.max(1, maxShell - 1); // ignore shell 0 in interpolation
    const t = d <= 1 ? 0 : (d - 1) / denom;
    return d3.interpolateLab(palette.shellNodeNear, palette.shellNodeFar)(t);
  };

  const edgeColourScale = (depth) => {
    const d = Math.max(0, Math.min(Number(depth) || 0, maxShell));
    const denom = Math.max(1, maxShell - 1); // ignore shell 0 in interpolation
    const t = d <= 1 ? 0 : (d - 1) / denom;
    return d3.interpolateLab(palette.shellEdgeNear, palette.shellEdgeFar)(t);
  };

  const nodeStrokeScale = (depth) => {
    const d = Math.max(0, Math.min(Number(depth) || 0, maxShell));
    const denom = Math.max(1, maxShell - 1); // ignore shell 0 in interpolation
    const t = d <= 1 ? 0 : (d - 1) / denom;
    return d3.interpolateLab(palette.nodeStrokeNear, palette.nodeStrokeFar)(t);
  };

  const forceNode = d3.forceManyBody().strength(-800);
  const forceEdge = d3
    .forceLink(edges)
    .id(({ index: i }) => N[i])
    .distance((d) => {
      return 100;
    });
  if (nodeStrength !== undefined) forceNode.strength(nodeStrength);
  if (edgeStrength !== undefined) forceEdge.strength(edgeStrength);

  const baseEdgeColor = palette.shellEdgeNear;
  const geneticStroke = palette.geneticStroke;
  const geneticFill = palette.geneticFill;
  const normalizeToArray = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (value instanceof Set) return Array.from(value);
    return [value];
  };

  const geneticSeqIdSet = useMemo(
    () =>
      new Set(
        normalizeToArray(geneticMatchSeqIds)
          .filter((id) => id !== null && id !== undefined && id !== "")
          .map((id) => String(id))
      ),
    [geneticMatchSeqIds]
  );

  const isGeneticMatch = (node) => {
    return (
      node.seq_id !== null &&
      node.seq_id !== undefined &&
      geneticSeqIdSet.has(String(node.seq_id))
    );
  };

  //should maybe add a viewbox?
  const ref = useD3Graph(
    (svg, nodes, edges) => {
      svg.selectAll("*").remove();
      svg
        .attr("width", width)
        .attr("height", height)
        .attr("viewBox", [-width / 2, -height / 2, width, height])
        .attr("preserveAspectRatio", "xMidYMid meet");

      const simulation = d3
        .forceSimulation(nodes)
        .force("link", forceEdge)
        .force("charge", forceNode)
        .force("center", d3.forceCenter())
        .force("x", d3.forceX(width / 2))
        .force("y", d3.forceY(height / 2))
        .on("tick", () => {
          edge
            .attr("x1", (d) => d.source.x)
            .attr("y1", (d) => d.source.y)
            .attr("x2", (d) => d.target.x)
            .attr("y2", (d) => d.target.y);
          node.attr("transform", (d) => `translate(${d.x},${d.y})`);
        });

      // Create a zoom behavior
      const zoom = d3Zoom
        .zoom()
        .scaleExtent([0.1, 10]) // Set the zoom scale limits
        .on("zoom", (event) => {
          svg.selectAll("g").attr("transform", event.transform);
          simulation.restart();
        });

      // Apply the zoom behavior to the SVG element
      svg.call(zoom);

      d3.selectAll("tooltip.graph-tooltip").remove();
      const tooltip = d3
        .select("body")
        .append("tooltip")
        .attr("class", "graph-tooltip")
        .style("opacity", 0)
        .style("background-color", palette.tooltipBg)
        .style("border-radius", "5px")
        .style("border", `1px solid ${palette.tooltipBorder}`)
        .style("padding", "8px")
        .style("color", palette.tooltipText)
        .style("position", "absolute")
        .style("font-size", "13px")
        .style("height", 0)
        .style("pointer-events", "none");

      svg
        .append("defs")
        .append("marker")
        .attr("id", "arrowhead")
        .attr("viewBox", "0 -5 10 10")
        .attr("refX", 10) // Adjusted refX to position arrowhead at the end
        .attr("refY", 0)
        .attr("markerWidth", 6)
        .attr("markerHeight", 6)
        .attr("orient", "auto")
        .append("path")
        .attr("d", "M0,-3L6,0L0,3")
        .attr("fill", baseEdgeColor)
        .attr("stroke", baseEdgeColor);

      const edge = svg
        .append("g")
        .selectAll("line")
        .data(edges)
        .join("line")
        .attr("marker-end", "url(#arrowhead)")
        .attr("stroke-linecap", "round")
        .attr("x1", (d) => d.source.x)
        .attr("y1", (d) => d.source.y)
        .attr("x2", (d) => {
          // Adjusted x2 to end before the node boundary
          const dx = d.target.x - d.source.x;
          const dy = d.target.y - d.source.y;
          const length = Math.sqrt(dx * dx + dy * dy);
          const targetX = d.target.x - (dx / length) * 50; // nodeRadius is the radius of your node
          return targetX;
        })
        .attr("y2", (d) => {
          // Adjusted y2 to end before the node boundary
          const dx = d.target.x - d.source.x;

          const dy = d.target.y - d.source.y;
          const length = Math.sqrt(dx * dx + dy * dy);

          const targetY = d.target.y - (dy / length) * 50;
          return targetY;
        });

      const nameById = new Map(nodes.map((n) => [String(n.id), n.name]));

      const minDur = Math.min(...ED);
      const maxDur = Math.max(...ED);
      const edgeWidths = edges.map((_, i) => {
        if (edge_width_param === "duration") {
          const range = maxDur - minDur;
          if (range === 0) return 3;
          return ((5 - 2.4) * (ED[i] - minDur)) / range + 2.4;
        }
        return 3;
      });
      const minWidth = edgeWidths.length ? Math.min(...edgeWidths) : 3;
      const maxWidth = edgeWidths.length ? Math.max(...edgeWidths) : 3;

      const opacityForWidth = (w) => {
        if (maxWidth === minWidth) return 0.7;
        const t = Math.max(
          0,
          Math.min(1, (w - minWidth) / (maxWidth - minWidth))
        );
        return 0.4 + t * (0.95 - 0.4);
      };

      edge.attr("stroke-width", ({ index: i }) => {
        const w = edgeWidths[i] ?? 3;
        return w;
      });

      const getEdgeColour = ({ index: i, source, target }) => {
        const colorMapByParam = {
          ward: colourMaps.wardColourMap(EW[i]),
          Hospital: colourMaps.hospColourMap(EH[i]),
          Specialty: colourMaps.specialtyColourMap(ESP[i]),
        };
        const depthSrc =
          shellDistance.get(String(source.id || source)) ?? maxShell;
        const depthTgt =
          shellDistance.get(String(target.id || target)) ?? maxShell;
        const depth = Math.min(depthSrc, depthTgt);
        return colorMapByParam[edge_colour_param] || edgeColourScale(depth);
      };

      edge
        .attr("stroke", (d) => getEdgeColour(d))
        .attr("stroke-opacity", function (d) {
          const w = edgeWidths[d.index] ?? 3;
          return opacityForWidth(w);
        })
        .each(function (d, i) {
          const clr = getEdgeColour(d);
          const w = edgeWidths[i] ?? 3;
          const markerOpacity = opacityForWidth(w);
          d3.select(this).attr("marker-end", `url(#arrowhead-${i})`);
          const marker = svg
            .select("defs")
            .append("marker")
            .attr("id", `arrowhead-${i}`)
            .attr("viewBox", "0 -5 10 10")
            .attr("refX", 10)
            .attr("refY", 0)
            .attr("markerWidth", 6)
            .attr("markerHeight", 6)
            .attr("orient", "auto");
          marker
            .append("path")
            .attr("d", "M0,-3L6,0L0,3")
            .attr("fill", clr)
            .attr("stroke", clr)
            .attr("fill-opacity", markerOpacity)
            .attr("stroke-opacity", markerOpacity);
        })
        .on("mousemove", function (event) {
          tooltip
            .style("left", event.pageX + 10 + "px")
            .style("top", event.pageY + 5 + "px");
        })
        .on("mouseover", function (event, d) {
          const duration = ED[d.index] || ED[edges.indexOf(d)] || "";
          const srcName = nameById.get(String(d.source.id || d.source)) || d.source.id || d.source;
          const tgtName = nameById.get(String(d.target.id || d.target)) || d.target.id || d.target;
          tooltip
            .html(
              `<strong>${srcName}</strong> → <strong>${tgtName}</strong><br/>Overlap duration: ${duration} day(s)`
            )
            .style("opacity", 0.9)
            .style("height", "auto");
        })
        .on("mouseleave", function () {
          tooltip.style("opacity", 0).style("height", 0);
        });

      const node = svg
        .append("g")
        .selectAll("g")
        .data(nodes)
        .join("g")
        .call(drag(simulation));

      node
        .append("circle")
        .attr("r", 10)
        .attr("fill", (d) => {
          if (String(d.id) === String(micro_id)) {
            return palette.index;
          }
          const shell = shellDistance.get(String(d.id)) ?? maxShell;
          if (geneticHighlightEnabled && isGeneticMatch(d)) {
            return geneticFill;
          }
          return shellColourScale(shell);
        })
        .attr("stroke", (d) => {
          if (String(d.id) === String(micro_id)) {
            return palette.index;
          }
          if (geneticHighlightEnabled && isGeneticMatch(d)) {
            return geneticStroke;
          }
          return nodeStrokeScale(shellDistance.get(String(d.id)) ?? maxShell);
        })
        .attr("stroke-opacity", 0.8)
        .attr("stroke-width", (d) => {
          if (String(d.id) === String(micro_id)) {
            return 4;
          }
          if (geneticHighlightEnabled && isGeneticMatch(d)) {
            return highlightPhylos ? 4 : 3;
          }
          return 2;
        })
        .on("mouseover", function () {
          d3.select(this)
            .transition()
            .duration(120)
            .attr("r", 12);
        })
        .on("mouseleave", function () {
          d3.select(this)
            .transition()
            .duration(120)
            .attr("r", 10);
        })
        .on("mousemove", function (event, d) {
          tooltip
            .style("left", event.pageX + 10 + "px")
            .style("top", event.pageY + 5 + "px");
        })
        .on("mouseover", (event, d) => {
          if (hoverToggled) {
            tooltip
              .style("opacity", 1)
              .style("height", "auto")
              .html("<b>" + d.name + "</b> (" + d.id + ")" +
                          // "<br>Location: " + source.ward + " <b><font color=#94aac7> [At Risk]</font></b>" +
                          "<br>Tested: " + d.inf_start
                          //  + " <font color=yellow>(" + daysAgo + " days ago)</font>" + (source.seq_id? "" : "<br>No sequencing data")
                          );
              // .html(
              //   `<strong>${d.name}</strong><br/>Patient ID: ${d.id}<br/>Infection start: ${d.inf_start}`
              // );
          }
        })
        .on("mouseleave", function () {
          tooltip.style("opacity", 0).style("height", 0);
        });

      node
        .append("text")
        .text(({ index: i }) => T[i])
        .attr("x", 14)
        .attr("y", "0.31em")
        .attr("font-size", 12)
        .attr("fill", (d) => {
          if (String(d.id) === String(micro_id)) {
            return palette.textIndex;
          }
          return geneticHighlightEnabled && isGeneticMatch(d)
            ? geneticStroke
            : palette.text;
        })
        // .attr("font-weight", 600)
        .on("click", (e, d) => {
          redirect(d.id);
        })
        .style("cursor", "pointer");
        
      // Add halo to text for readability
      const label = node
        .append("text")
        .text(({ index: i }) => T[i])
        .attr("x", 14)
        .attr("y", "0.31em")
        .attr("font-size", 12)
        .attr("paint-order", "stroke")
        .attr("stroke", "white")
        .attr("stroke-width", 3.5)
        .attr("stroke-linejoin", "round")
        .attr("stroke-opacity", 0.7)
        .attr("fill", (d) => {
          if (String(d.id) === String(micro_id)) return palette.textIndex;
          return geneticHighlightEnabled && isGeneticMatch(d) ? geneticStroke : palette.text;
        })
        .on("click", (e, d) => redirect(d.id))
        .style("cursor", "pointer");

      const redirect = (micro_id) => {
        navigate(`/patient/${micro_id}`);
      };

      //if (T) node.append("title").text(({ index: i }) => T[i]);

      function drag(simulation) {
        function dragStarted(event) {
          if (!event.active) simulation.alphaTarget(0.3).restart();
          event.subject.fx = event.subject.x;
          event.subject.fy = event.subject.y;
        }
        function dragged(event) {
          event.subject.fx = event.x;
          event.subject.fy = event.y;
        }
        function dragEnded(event) {
          if (!event.active) simulation.alphaTarget(0);
          event.subject.fx = null;
          event.subject.fy = null;
        }
        return d3
          .drag()
          .on("start", dragStarted)
          .on("drag", dragged)
          .on("end", dragEnded);
      }
    },
    nodes,
    edges
  );

  return (
    <div ref={containerRef} style={{ width: "100%", height: "100%" }}>
      <svg ref={ref} style={{ width: "100%", height: "100%" }}></svg>
    </div>
  );
}
